<template>
  <!-- prettier-ignore -->
  <div
    id="dvpSessionMenu"
    class="menu"
  >
    <Form.Button
      class="plain parent"
      :class="{'active': isCardActive}"
      icon-art="light"
      icon="user"
      @click="toggleCard"
    />
    <template v-if="isCardActive">
      <Transition
        appear
        name="pop">
        <div class="menu-card">
          <div
            v-if="isContactDataEditable"
            class="menu-item">
            <Form.Button
              link
              :text="$t('screen.sessionmenu.profile')"
              @click="isOnboardingOpen = true"
            />
          </div>

          <div
            v-if="showRestart"
            class="menu-item">
            <Form.Button
              link
              :text="$t('screen.sessionmenu.restart')"
              data-track-as="restart"
              :data-track-text="CHANNEL.OVP"
              @click="restartApplication()"
            />
          </div>

          <div class="menu-item languages">
            <Form.Button
              v-for="language in shownLanguages"
              :key="language.value"
              plain
              :text="language.text"
              @click="changeLanguage(language.value)"
            />
            <div class="button button--empty" />
          </div>
        </div>
      </Transition>
    </template>

    <the-form-modal
      v-slot="{ modalProps }"
      v-model="isOnboardingOpen"
      locked>
      <contact-data-form
        @close="modalProps.close"
      />
    </the-form-modal>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { events$, loading$ } from '@/services'

import ContactDataForm from '@/components/Onboarding/ContactDataForm'
import * as Form from '@/components/Form'

import { useAuth, useBasket, useI18n, useStatus, useTranslation } from '@/hooks'

import { CHANNEL, ROUTE_NAME, SUPPORTED_LANGUAGES } from '@/config/constants'
import { EVENT_ONBOARDING } from '@/config/events'

// HOOKS
const { restartApplication } = useAuth()
const { isOnboardingSecured } = useStatus()
const { basket, isTemporaryBasket } = useBasket()
const { updateTranslations } = useTranslation()
const { locale } = useI18n()
const route = useRoute()

// DATA
const isCardActive = ref(false)
const isOnboardingOpen = ref(false)

// COMPUTED
const isContactDataEditable = computed(() => {
  return isOnboardingSecured.value && !basket.existingCustomer && route.name !== ROUTE_NAME.ONBOARDING
})

const shownLanguages = computed(() => {
  return SUPPORTED_LANGUAGES.filter(language => {
    return language.value !== locale.value
  })
})

const showRestart = computed(() => {
  return !isTemporaryBasket()
})

// METHODS
async function changeLanguage(lang) {
  loading$.start()
  await updateTranslations({ locale: lang, hasChanged: true })
  loading$.end()
}

function registerCloseHandler(event) {
  const isInsideTarget = !!event.target.closest('#dvpSessionMenu')
  if (!isInsideTarget) {
    isCardActive.value = false
  }
}

function toggleCard() {
  isCardActive.value = !isCardActive.value
}

// WATCHER
watch(isCardActive, isActive => {
  if (isActive) {
    document.addEventListener('click', registerCloseHandler)
    document.addEventListener('scroll', toggleCard)
  } else {
    document.removeEventListener('click', registerCloseHandler)
    document.removeEventListener('scroll', toggleCard)
  }
})

// LIFECYCLE HOOKS
onMounted(() => {
  events$.on(EVENT_ONBOARDING.OPEN, () => {
    isOnboardingOpen.value = true
  })
})
</script>

<style name="animations" scoped>
@keyframes pop {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

.pop-enter-active {
  animation: pop 0.3s;
}

.pop-leave-active {
  animation: pop 0.3s reverse;
}
</style>

<style name="mobile" scoped>
.menu .button.plain {
  min-width: auto;
  width: auto;
  margin: 0;
  padding: 0;
  font-size: 16px;

  &:hover :deep(.icon) {
    color: var(--c-primary-color-2);
  }

  :deep(.icon) {
    color: var(--c-primary-neutral-1);
  }

  &.active :deep(.icon) {
    color: var(--c-primary-color-2);
  }

  :deep(.icon + span) {
    margin: 0;
  }
}

.menu-card {
  background-color: var(--c-primary-neutral-2);
  display: flex;
  content: '';
  gap: 1px;

  height: auto;
  width: 100%;
  position: absolute;
  z-index: 5;
  padding: 1px;
  right: 0px;
  top: 45px;

  flex-direction: column;
  transform-origin: top right;
  transition: top 0.25s ease-in-out;
}

.menu-item {
  background-color: var(--c-primary-neutral-3);
  padding: 16px;

  .button,
  a,
  .icon {
    font-weight: normal;
    color: var(--c-primary-neutral-1);
  }

  .button {
    min-width: auto;
    width: auto;
    padding: 0;
    min-height: auto;
    height: auto;
    align-items: initial;
  }
}

.menu-item.languages {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  background-color: var(--c-primary-neutral-2);
  gap: 1px;
  padding: 0;

  .button {
    /*flex: 1 1 auto;*/

    padding: 4px 8px;
    height: 48px;
    background-color: var(--c-primary-neutral-3);
    border-radius: 0;
    width: 48px;

    &--empty {
      flex: 1 1 auto;
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .menu-card {
    right: initial;
    margin-left: -265px;
    width: 320px;

    transition: none;
  }
}
</style>
